import React, { useEffect } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useTeamContext } from "../TeamContext";
import "../styles.css";

ChartJS.register(ArcElement, Tooltip, Legend);

function DistributionAdjustmentQuestion({ workspace }) {
    const { updateWorkspaceData } = useTeamContext();
    const wsP1 = workspace.part1Answers;
    const wsP2 = workspace.part2Answers;

    // Calculate active contribution categories first
    const totalCash = Object.values(wsP1.foundersCashInvested || {}).reduce((a, b) => a + (b || 0), 0);
    const totalTime = Object.values(wsP1.foundersTime || {}).reduce((a, b) => a + (b || 0), 0);
    const totalAssets = Object.values(wsP1.foundersAssets || {}).reduce((a, b) => a + (b || 0), 0);

    const activeCategories = {
        financial: totalCash > 0,
        time: totalTime > 0,
        assets: totalAssets > 0
    };

    const activeKeys = [
        activeCategories.financial && "Capital Investment (Financial)",
        activeCategories.time && "Time Commitment",
        activeCategories.assets && "Capital Investment (Assets)"
    ].filter(Boolean);

    // Now declare derived values once
    const finalDist = wsP2.equityDistributionAdjustment || {};
    const values = activeKeys.map(k => finalDist[k] || 0);
    const sum = values.reduce((a, b) => a + b, 0);
    const unallocated = 100 - sum;

    // Auto-update distribution when Q7-9 ratings change
    useEffect(() => {
        const R_FI = activeCategories.financial ? (wsP1.ratingFinancialImpact || 0) : 0;
        const R_TC = activeCategories.time ? (wsP1.ratingTimeImportance || 0) : 0;
        const R_AI = activeCategories.assets ? (wsP1.ratingAssetImportance || 0) : 0;

        const totalRating = R_FI + R_TC + R_AI;
        const newDist = {};

        if (totalRating > 0) {
            activeCategories.financial &&
            (newDist["Capital Investment (Financial)"] = (R_FI / totalRating) * 100);
            activeCategories.time &&
            (newDist["Time Commitment"] = (R_TC / totalRating) * 100);
            activeCategories.assets &&
            (newDist["Capital Investment (Assets)"] = (R_AI / totalRating) * 100);
        } else {
            const activeCount = Object.values(activeCategories).filter(Boolean).length;
            const equalWeight = activeCount ? 100 / activeCount : 0;
            activeCategories.financial &&
            (newDist["Capital Investment (Financial)"] = equalWeight);
            activeCategories.time &&
            (newDist["Time Commitment"] = equalWeight);
            activeCategories.assets &&
            (newDist["Capital Investment (Assets)"] = equalWeight);
        }

        updateWorkspaceData(workspace.id, {
            ...workspace,
            part2Answers: {
                ...wsP2,
                equityDistributionAdjustment: newDist
            }
        });
    }, [
        wsP1.ratingFinancialImpact,
        wsP1.ratingTimeImportance,
        wsP1.ratingAssetImportance,
        totalCash,
        totalTime,
        totalAssets
    ]);

    function handleChangeSlice(key, newVal) {
        const numericVal = parseFloat(newVal) || 0;
        const updated = { ...(wsP2.equityDistributionAdjustment || {}) };

        updated[key] = numericVal;

        const currentTotal = Object.values(updated).reduce((a, b) => a + b, 0);
        if (currentTotal > 100) {
            const overAmount = currentTotal - 100;
            updated[key] = numericVal - overAmount;
        }

        updateWorkspaceData(workspace.id, {
            ...workspace,
            part2Answers: {
                ...wsP2,
                equityDistributionAdjustment: updated
            }
        });
    }

    const chartData = {
        labels: activeKeys,
        datasets: [{
            data: values,
            backgroundColor: ["#F97316", "#10B8A6", "#3B82F6"].slice(0, activeKeys.length)
        }]
    };

    return (
        <div style={{color: "#28435a"}}>
            <h3>Question 10/15</h3>
            <p>
                Does the calculated distribution accurately reflect the impact of financial investment, time commitment,
                and asset contribution on your business? If not, feel free to adjust the distribution to better reflect
                your business's reality.
                {sum > 100 && " (Last adjustment clamped to prevent exceeding 100%)"}
            </p>

            <div style={{
                backgroundColor: "#FAFAFA",
                padding: "16px",
                borderRadius: "6px",
                marginBottom: "16px",
                border: "1px solid #ECECEC"
            }}>
                {activeKeys.map((k) => (
                    <div key={k} style={{marginBottom: "12px"}}>
                        <strong>{k}:</strong> {finalDist[k]?.toFixed(1) || 0}%
                        <input
                            type="range"
                            style={{marginLeft: "8px", width: "280px"}}
                            min={0}
                            max={100}
                            step={0.5}
                            value={finalDist[k] || 0}
                            onChange={(e) => handleChangeSlice(k, e.target.value)}
                        />
                    </div>
                ))}
            </div>

            <div style={{height: "280px", marginTop: "16px"}}>
                <h4 style={{marginBottom: "8px"}}>Distribution Breakdown</h4>
                <Pie data={chartData} options={{maintainAspectRatio: false}}/>
            </div>
            <div className="allocation-summary">
                <div className="total-allocation">
                    <span>Total Allocated: {sum.toFixed(1)}%</span>
                    <span style={{color: unallocated !== 0 ? '#df1c1c' : '#22cb4d'}}>
                        ({unallocated.toFixed(1)}% Unallocated)
                    </span>
                </div>
            </div>
        </div>
    );
}

export default DistributionAdjustmentQuestion;
